import { CameraRotate, CaretDown, ChatTeardropText, Compass, CrosshairSimple, Cube, CursorClick, DotsSixVertical, Hand, Lightbulb, LinkBreak, MapPin, Ruler, Sun, TreeStructure } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { canStackObject, MAX_OBJECT_HIERARCHY_DEPTH } from '../lib/objectHierarchy';
import { getRoomPolygon } from '../lib/roomGeometry';
import type { PlacedObject, ProjectSnapshot, SunPosition, ToolMode } from '../types';

interface SceneChromeProps {
  tool: ToolMode;
  project: ProjectSnapshot;
  selectedId: string | null;
  readOnly: boolean;
  dialogueMode: boolean;
  cameraView: 'perspective' | 'top' | 'front';
  onTool: (tool: ToolMode) => void;
  onDialogueMode: (enabled: boolean) => void;
  onCamera: (view: 'perspective' | 'top' | 'front') => void;
  onFocusObject: (id: string) => void;
  onStackObject: (id: string, supportId: string) => void;
  onDetachObject: (id: string) => void;
  onRoom: () => void;
  lightingMode: 'indoor' | 'outdoor';
  onLightingMode: (mode: 'indoor' | 'outdoor') => void;
  sunPosition: SunPosition;
  onSunPosition: (position: SunPosition) => void;
}

export function SceneChrome({ tool, project, selectedId, readOnly, dialogueMode, cameraView, onTool, onDialogueMode, onCamera, onFocusObject, onStackObject, onDetachObject, onRoom, lightingMode, onLightingMode, sunPosition, onSunPosition }: SceneChromeProps) {
  const [objectListOpen, setObjectListOpen] = useState(false);
  const [draggedObjectId, setDraggedObjectId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const objectNavigatorRef = useRef<HTMLDivElement>(null);
  const draggedObjectIdRef = useRef<string | null>(null);
  const dropTargetIdRef = useRef<string | null>(null);
  const objectIndex = useMemo(() => new Map(project.objects.map((object, index) => [object.id, index])), [project.objects]);
  const objectById = useMemo(() => new Map(project.objects.map((object) => [object.id, object])), [project.objects]);
  const childrenBySupport = useMemo(() => {
    const children = new Map<string, PlacedObject[]>();
    project.objects.forEach((object) => {
      if (!object.supportId || !objectById.has(object.supportId)) return;
      const group = children.get(object.supportId) ?? [];
      group.push(object);
      children.set(object.supportId, group);
    });
    return children;
  }, [objectById, project.objects]);
  const rootObjects = useMemo(
    () => project.objects.filter((object) => !object.supportId || !objectById.has(object.supportId)),
    [objectById, project.objects],
  );

  const canStackOn = (id: string, supportId: string) => {
    return canStackObject(project.objects, id, supportId);
  };

  const clearTreeDrag = () => {
    draggedObjectIdRef.current = null;
    dropTargetIdRef.current = null;
    setDraggedObjectId(null);
    setDropTargetId(null);
  };

  const renderObjectTree = (object: PlacedObject) => {
    const children = childrenBySupport.get(object.id) ?? [];
    const isDropTarget = !!draggedObjectId && dropTargetId === object.id && canStackOn(draggedObjectId, object.id);
    return (
      <li key={object.id} className="objects-tree-node">
        <div
          className={`objects-tree-row ${selectedId === object.id ? 'selected' : ''} ${draggedObjectId === object.id ? 'dragging' : ''} ${isDropTarget ? 'drop-target' : ''}`}
          data-object-tree-id={object.id}
        >
          <span
            className="objects-tree-drag"
            role="button"
            tabIndex={0}
            aria-label={`${object.name} 계층 이동`}
            title="누른 채 다른 오브젝트로 이동"
            onPointerDown={(event) => {
              if (readOnly) return;
              event.preventDefault();
              event.stopPropagation();
              event.currentTarget.setPointerCapture(event.pointerId);
              draggedObjectIdRef.current = object.id;
              setDraggedObjectId(object.id);
            }}
            onPointerMove={(event) => {
              const draggedId = draggedObjectIdRef.current;
              if (!draggedId) return;
              const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-object-tree-id]')?.dataset.objectTreeId ?? null;
              const nextTarget = target && canStackOn(draggedId, target) ? target : null;
              if (dropTargetIdRef.current === nextTarget) return;
              dropTargetIdRef.current = nextTarget;
              setDropTargetId(nextTarget);
            }}
            onPointerUp={(event) => {
              event.preventDefault();
              event.stopPropagation();
              const draggedId = draggedObjectIdRef.current;
              const targetId = dropTargetIdRef.current;
              if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
              if (draggedId && targetId && canStackOn(draggedId, targetId)) onStackObject(draggedId, targetId);
              clearTreeDrag();
            }}
            onPointerCancel={clearTreeDrag}
          >
            <DotsSixVertical size={15} weight="bold" aria-hidden="true" />
          </span>
          <button
            className="objects-tree-focus"
            type="button"
            aria-current={selectedId === object.id ? 'true' : undefined}
            onClick={() => onFocusObject(object.id)}
          >
            <span className="objects-navigator-index">{String((objectIndex.get(object.id) ?? 0) + 1).padStart(2, '0')}</span>
            <span className="objects-navigator-copy">
              <strong>{object.name}</strong>
              <small>{object.supportId ? '상단 배치' : `X ${object.position[0].toFixed(2)} · Z ${object.position[2].toFixed(2)} m`}</small>
            </span>
            <CrosshairSimple size={17} weight="bold" aria-hidden="true" />
          </button>
          {object.supportId && !readOnly && (
            <button
              className="objects-tree-detach"
              type="button"
              aria-label={`${object.name}을(를) 상위 오브젝트에서 분리`}
              title="상위 오브젝트에서 분리"
              onClick={(event) => {
                event.stopPropagation();
                onDetachObject(object.id);
              }}
            >
              <LinkBreak size={15} weight="bold" aria-hidden="true" />
            </button>
          )}
        </div>
        {children.length > 0 && <ol className="objects-tree-children">{children.map(renderObjectTree)}</ol>}
      </li>
    );
  };

  useEffect(() => {
    if (!objectListOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !objectNavigatorRef.current?.contains(event.target)) setObjectListOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setObjectListOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [objectListOpen]);

  return (
    <>
      <div className="scene-tools" aria-label="편집 도구" role="toolbar">
        <button
          className={tool === 'select' && !dialogueMode ? 'active' : ''}
          type="button"
          onClick={() => onTool('select')}
          aria-label="선택 및 이동"
          aria-keyshortcuts="V"
          aria-pressed={tool === 'select' && !dialogueMode}
          title="선택 및 이동 (V)"
        >
          <CursorClick size={19} /><kbd>V</kbd>
        </button>
        <button
          className={tool === 'orbit' && !dialogueMode ? 'active' : ''}
          type="button"
          onClick={() => onTool('orbit')}
          aria-label="화면 평행이동"
          aria-keyshortcuts="H"
          aria-pressed={tool === 'orbit' && !dialogueMode}
          title="화면 평행이동 (H)"
        >
          <Hand size={19} /><kbd>H</kbd>
        </button>
        <button
          className={dialogueMode ? 'active' : ''}
          type="button"
          onClick={() => onDialogueMode(true)}
          aria-label="대화 모드"
          aria-pressed={dialogueMode}
          title="대화 모드"
        >
          <ChatTeardropText size={19} weight={dialogueMode ? 'fill' : 'regular'} />
        </button>
        <span className="tool-divider" />
        <div className="lighting-switch" role="radiogroup" aria-label="조명 환경">
          <button type="button" role="radio" aria-checked={lightingMode === 'indoor'} className={lightingMode === 'indoor' ? 'active' : ''} onClick={() => onLightingMode('indoor')} title="실내 형광등"><Lightbulb size={17} weight={lightingMode === 'indoor' ? 'fill' : 'regular'} /><span>실내</span></button>
          <button type="button" role="radio" aria-checked={lightingMode === 'outdoor'} className={lightingMode === 'outdoor' ? 'active' : ''} onClick={() => onLightingMode('outdoor')} title="실외 자연광"><Sun size={17} weight={lightingMode === 'outdoor' ? 'fill' : 'regular'} /><span>실외</span></button>
        </div>
      </div>
      {lightingMode === 'outdoor' && (
        <div className="sun-position-panel" aria-label="태양 위치 설정">
          <div className="sun-position-heading"><Sun size={17} weight="fill" /><strong>태양 위치</strong><span>{sunPosition.azimuth}° / {sunPosition.elevation}°</span></div>
          <label><span><Compass size={14} /> 방위</span><input type="range" min="0" max="359" step="1" value={sunPosition.azimuth} onChange={(event) => onSunPosition({ ...sunPosition, azimuth: Number(event.target.value) })} /></label>
          <label><span><Sun size={14} /> 고도</span><input type="range" min="10" max="85" step="1" value={sunPosition.elevation} onChange={(event) => onSunPosition({ ...sunPosition, elevation: Number(event.target.value) })} /></label>
        </div>
      )}
      <div className="scene-top-actions" ref={objectNavigatorRef}>
        <button
          className={`objects-navigator-button ${objectListOpen ? 'active' : ''}`}
          type="button"
          aria-expanded={objectListOpen}
          aria-controls="installed-object-list"
          onClick={() => setObjectListOpen((open) => !open)}
        >
          <Cube size={16} weight="duotone" />
          <span>설치 오브젝트</span>
          <strong>{project.objects.length}</strong>
          <CaretDown className="objects-navigator-caret" size={12} weight="bold" />
        </button>
        <button className="room-chip" type="button" onClick={onRoom}>
          <Ruler size={16} />
          <span>{getRoomPolygon(project.room).length === 4 ? `${project.room.width} × ${project.room.depth} × ${project.room.height} m` : `${getRoomPolygon(project.room).length}각형 · 벽 ${project.room.height} m`}</span>
        </button>
        {objectListOpen && (
          <section id="installed-object-list" className="objects-navigator-popover" aria-label="설치된 오브젝트 목록">
            <header>
              <div>
                <span>현재 공간</span>
                <strong><TreeStructure size={16} weight="duotone" /> 설치된 오브젝트</strong>
              </div>
              <small>{project.objects.length}개</small>
            </header>
            {project.objects.length ? (
              <ol className="objects-tree-root">{rootObjects.map(renderObjectTree)}</ol>
            ) : (
              <div className="objects-navigator-empty"><Cube size={24} /><strong>설치된 오브젝트가 없습니다</strong><span>왼쪽 라이브러리에서 오브젝트를 배치해 보세요.</span></div>
            )}
            <p>다른 오브젝트로 드래그해 상단에 배치할 수 있습니다. 계층은 최대 {MAX_OBJECT_HIERARCHY_DEPTH}단계입니다.</p>
          </section>
        )}
      </div>
      {!dialogueMode && (
        <div className="camera-switcher" aria-label="카메라 시점">
          <CameraRotate size={18} />
          {(['perspective', 'top', 'front'] as const).map((view) => (
            <button key={view} className={cameraView === view ? 'active' : ''} type="button" onClick={() => onCamera(view)}>
              {view === 'perspective' ? '입체' : view === 'top' ? '평면' : '정면'}
            </button>
          ))}
        </div>
      )}
      <div className="scene-status">
        {dialogueMode ? (
          <>
            <span><ChatTeardropText size={14} /> 대화 모드</span>
            <span>시점 이동 후 시트에서 캡처</span>
          </>
        ) : tool === 'orbit' ? (
          <>
            <span><Hand size={14} /> 드래그 평행이동</span>
            <span>우클릭 회전 / 방향키 이동</span>
          </>
        ) : (
          <>
            <span><MapPin size={14} /> 바닥 기준 50 mm 스냅</span>
            <span>오브젝트 {project.objects.length}개</span>
          </>
        )}
      </div>
    </>
  );
}
